import React, { useMemo } from 'react';
import cc from 'classcat';
import { GetServerSideProps } from 'next';
import { useQuery } from 'urql';

import { Button, CardGrid, Image } from '@/components';
import HeroImage from '@/components/layout/HeroImage';
import { useIsMobile } from '@/hooks';
import { getServerSidePropsWithGlobalData } from '@/lib/getServerSideProps';
import { extractFragment, extractItems } from '@/lib/GraphQLHelper';
import { withUrql } from '@/lib/urqlClient';
import { ErrorPage } from '@/pages/_error';

import query from './query.graphql';

import styles from './styles.module.scss';

const About = ({}) => {
  const [{ data, error, fetching }] = useQuery<AboutEntryQuery, AboutEntryQueryVariables>({ query });

  const [isMobile] = useIsMobile(768);
  const aboutPage = extractFragment<AboutPageEntryFragment>(data?.entry);

  const offers = useMemo(
    () =>
      aboutPage?.aboutPageContent?.filter(
        (r) => r?.__typename === 'aboutPageContent_offers_BlockType',
      ) as unknown as AboutPageContentOfferFragment[],
    [aboutPage?.aboutPageContent],
  );

  const gallery = useMemo(
    () =>
      aboutPage?.aboutPageContent?.filter(
        (r) => r?.__typename === 'aboutPageContent_gallery_BlockType',
      ) as unknown as AboutPageContentGalleryFragment[],
    [aboutPage?.aboutPageContent],
  );

  const products = useMemo(
    () =>
      aboutPage?.aboutPageContent?.filter(
        (r) => r?.__typename === 'aboutPageContent_products_BlockType',
      ) as unknown as AboutPageContentProductsFragment[],
    [aboutPage?.aboutPageContent],
  );

  if (error && !fetching) {
    return <ErrorPage statusCode={500} err={error} />;
  }

  return (
    <div className="basicContainer">
      <div className={styles.about}>
        <HeroImage image={aboutPage?.heroImage?.[0]?.url}>
          <div className={styles.top}>
            {aboutPage?.body && <div dangerouslySetInnerHTML={{ __html: aboutPage.body }} />}
          </div>
        </HeroImage>
        <div className={styles.body}>
          {offers?.map((r) => (
            <div key={r.body} className={cc([styles.reason, styles.reasonOne])}>
              <div className={styles.left}>
                {r?.body && <div dangerouslySetInnerHTML={{ __html: r.body }} />}
                <ul>
                  {extractItems<OfferFragment>(r?.offers)?.map((o) => (
                    <li key={o!.title}>
                      {o!.title} {o?.description && `- ${o.description}`}
                    </li>
                  ))}
                </ul>
              </div>
              <div className={styles.right}>
                {extractItems<OfferFragment>(r?.offers)?.map(
                  (o) =>
                    o?.logos?.[0]?.webp && (
                      <a
                        key={o?.logos?.[0]?.webp}
                        href={`/offers/${extractItems<CategoryFragment>(o?.categories)?.[0]?.slug}/${o!.slug}`}
                      >
                        <Image {...o?.logos?.[0]} />
                      </a>
                    ),
                )}
              </div>
            </div>
          ))}
          {gallery?.map((r) => (
            <div key={r.body} className={cc([styles.reason, styles.reasonTwo])}>
              <div className={styles.left}>
                {r.body && <div dangerouslySetInnerHTML={{ __html: r.body }} />}
                <div className={styles.bottom}>
                  {!isMobile && (
                    <div className={styles.images}>
                      {r?.gallery?.[3]?.url && <img src={r.gallery[3].url} alt="" />}
                      {r?.gallery?.[4]?.url && <img src={r.gallery[4].url} alt="" />}
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.right}>
                {!isMobile ? (
                  <>
                    <div className={styles.topImages}>
                      {r?.gallery?.[0]?.url && <img src={r.gallery[0].url} alt="" />}
                      {r?.gallery?.[1]?.url && <img src={r.gallery[1].url} alt="" />}
                    </div>
                    <div className={styles.bottomImage}>
                      {r?.gallery?.[2]?.url && <img src={r.gallery[2].url} alt="" />}
                    </div>
                  </>
                ) : (
                  <div className={styles.mobileGallery}>
                    <div>
                      {r?.gallery?.[3]?.url && <img src={r.gallery[3].url} alt="" />}
                      {r?.gallery?.[4]?.url && <img src={r.gallery[4].url} alt="" />}
                    </div>
                    {r?.gallery?.[1]?.url && <img src={r.gallery[1].url} alt="" />}
                    <div>
                      {r?.gallery?.[2]?.url && <img src={r.gallery[2].url} alt="" />}
                      {r?.gallery?.[0]?.url && <img src={r.gallery[0].url} alt="" />}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
          {products?.map((r: AboutPageContentProductsFragment) => (
            <div key={r.body} className={cc([styles.reason, styles.reasonThree])}>
              <div className={styles.left}>
                {r.body && <div dangerouslySetInnerHTML={{ __html: r.body }} />}
                <div className={styles.buttonContainer}>
                  <Button href="/shop">View our products</Button>
                </div>
              </div>
              <div className={`${styles.right} overflowMobile`}>
                <CardGrid cards={r?.products as ETicketFragment[]} slider={isMobile} small={isMobile} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) =>
  await getServerSidePropsWithGlobalData(ctx, async ({ urqlClient }) => {
    await urqlClient.query<AboutEntryQuery, AboutEntryQueryVariables>(query, {}).toPromise();

    return { props: undefined };
  });

export default withUrql(About);
